import React, { useState, useEffect, useRef } from 'react';
import endpoints from '../../define/endpoints';
import ModalDrawer from './ModalDrawer';
import PropTypes from 'prop-types';
import Button, { ButtonSizes, ButtonStyles } from '../Button/Button';
import BetaIcon, { IconColors, IconSizes } from '../Icons/Icon';
import { Timeline } from '../Timeline/Timeline';
import { useMatchesMinWidth } from '../../hooks/useMatchesMinWidth';
import { TRANSLATIONS } from '../../../translations';
import { BREAKPOINTS } from '../../../constants';
import { ModalConfirmedVoucherToGiftcard } from './ModalConfirmedVoucherToGiftcard';
import Dialog, { DialogAlignments, DialogTypes } from '../Dialog/Dialog';
import Typography, {
  TypographyVariants,
  TypographyWeights,
} from '../../ui-components/Typography/Typography';
import { ModalCancelVoucherForm } from './ModalCancelVoucherForm';
import { ModalOrderCard } from './subComponents/ModalOrderCard';

const ModalCancelConfirmedVoucher = ({
  order,
  isOpen,
  setIsOpen,
  token,
  setRequestDetails,
}) => {
  if (!order || !token) return null;

  const orderId = order.id;
  const hasMounted = useRef(false);
  const isDeviceSmallerThanMblLandscape = !useMatchesMinWidth(
    BREAKPOINTS.MBL_LANDSCAPE
  );

  const [step, setStep] = useState(1);
  const [isModalrefundOpen, setIsModalRefundOpen] = useState(false);
  const [isModalTransformVoucherOpen, setIsModalTransformVoucherOpen] =
    useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  useEffect(() => {
    document.body.classList.toggle('noscroll', isOpen);
    return () => document.body.classList.remove('noscroll');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
    }
  }, [isOpen]);

  useEffect(() => {
    if (step === 1) {
      setIsCancelling(false);
    }
  }, [step]);

  const proceed = () => {
    setStep(step + 1);
  };

  const goBack = () => {
    setStep(step - 1);
  };

  const timelineSteps = [
    {
      icon: 'message',
      status: 'approved',
      title: {
        text: TRANSLATIONS.SENDING_REQUEST,
        bold: true,
      },
    },
    {
      icon: 'spinner-dotted',
      status: 'pending',
      title: {
        text: TRANSLATIONS.PROCESSING,
        bold: true,
      },
    },
    {
      icon: 'document',
      status: 'pending',
      title: {
        text: TRANSLATIONS.OUTCOME,
        bold: true,
      },
    },
  ];

  const firstStepModal = (
    <ModalDrawer
      isOpen={isOpen && step === 1}
      setIsOpen={setIsOpen}
      isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
      modalCustomWidth={640}
      title={TRANSLATIONS.ACTIVITY_CANCEL_REQUEST}
      content={
        <div className="pl-m pr-m pt-xl pb-xl w-100% d-flex flex-column gap-xl">
          <Typography
            classNames="text-left"
            variant={TypographyVariants.p2}
            weight={TypographyWeights.bold}
          >
            {TRANSLATIONS.WANT_TO_PROCEED_WITH_VOUCHER_CANCELLATION_REQUEST}
          </Typography>

          <ModalOrderCard order={order} compact />

          <Typography
            classNames="text-left"
            variant={TypographyVariants.p3}
            dangerouslySetInnerHTML={{
              __html:
                TRANSLATIONS.BY_CANCELING_THIS_VOUCHER_YOU_WILL_LOSE_THE_OPPORTUNITY_TO_USE_IT,
            }}
          />
          <div>
            <Dialog
              align={DialogAlignments.row}
              type={DialogTypes.feature}
              content={TRANSLATIONS.OFFER_GIFTCARD}
              icon={
                <BetaIcon
                  name={'lightBulb'}
                  color={IconColors.regular}
                  size={IconSizes[24]}
                />
              }
            />
          </div>
        </div>
      }
      footer={
        <div className="d-flex gap-m pb-m pl-xl pr-xl">
          <Button
            classNames={'flex-1 pr-m pl-m'}
            size={ButtonSizes.s}
            style={ButtonStyles.secondary}
            label={TRANSLATIONS.TRANSFORM_TO_GIFTCARD}
            onClick={() => {
              setIsModalTransformVoucherOpen(true);
              proceed();
            }}
          />
          <Button
            classNames={'flex-1 pr-m pl-m'}
            size={ButtonSizes.s}
            style={ButtonStyles.empty}
            label={TRANSLATIONS.CONTINUE}
            onClick={() => {
              setIsCancelling(true);
              proceed();
            }}
          />
        </div>
      }
    />
  );

  const secondStepModal = (
    <ModalDrawer
      isOpen={isOpen && step === 2}
      setIsOpen={setIsOpen}
      goBack={() => goBack()}
      isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
      modalCustomWidth={640}
      title={TRANSLATIONS.ACTIVITY_CANCEL_REQUEST}
      content={
        <div className="pl-m pr-m pt-xl pb-xl w-100% d-flex flex-column gap-xl">
          <Timeline timelineSteps={timelineSteps} />
          <div>
            <Dialog
              align={DialogAlignments.row}
              type={DialogTypes.feature}
              content={TRANSLATIONS.CANCEL_VOUCHER_INSTRUCTIONS}
              icon={
                <BetaIcon
                  name={'info'}
                  color={IconColors.regular}
                  size={IconSizes[24]}
                />
              }
            />
          </div>
        </div>
      }
      footer={
        <div className="d-flex gap-m pb-m pl-xl pr-xl">
          <Button
            classNames={'flex-1 pr-m pl-m'}
            size={ButtonSizes.s}
            style={ButtonStyles.empty}
            label={TRANSLATIONS.CANCEL}
            onClick={() => setIsOpen(false)}
          />
          <Button
            classNames={'flex-1 pr-m pl-m'}
            size={ButtonSizes.s}
            style={ButtonStyles.secondary}
            label={TRANSLATIONS.CONTINUE}
            onClick={() => {
              setIsModalRefundOpen(true);
              proceed();
            }}
          />
        </div>
      }
    />
  );
  //Form modal
  const thirdStepModal = (
    <ModalCancelVoucherForm
      isOpen={isOpen && step === 3}
      setIsOpen={setIsOpen}
      goBack={() => goBack()}
      token={token}
      orderId={orderId}
      isDeviceSmallerThanMblLandscape={isDeviceSmallerThanMblLandscape}
      setRequestDetails={setRequestDetails}
    />
  );

  const modalConfirmedVoucherToGiftcard = (
    <ModalConfirmedVoucherToGiftcard
      isOpen={isModalTransformVoucherOpen}
      setIsOpen={setIsModalTransformVoucherOpen}
      order={order}
      token={token}
      setRequestDetails={setRequestDetails}
      onCancel={() => {
        setIsOpen(false);
      }}
    />
  );

  return (
    <>
      {firstStepModal}
      {step === 2 &&
        !isModalTransformVoucherOpen &&
        isCancelling &&
        secondStepModal}
      {step === 2 && !isCancelling && modalConfirmedVoucherToGiftcard}
      {thirdStepModal}
    </>
  );
};

ModalCancelConfirmedVoucher.propTypes = {};

export { ModalCancelConfirmedVoucher };
